import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { Caricamento, Errore, Etichetta, Scheda, Vuoto, useCarico } from '../componenti/base';
import { formattaData } from '../formato';
import { useSessione } from '../main';

interface VoceRegistro {
  id: string;
  entita: string;
  entitaId: string | null;
  azione: string;
  nota: string | null;
  dettaglio: unknown;
  utenteNome: string;
  origine: 'PERSONA' | 'SISTEMA';
  ip: string | null;
  creatoIl: string;
}
interface Pagina {
  righe: VoceRegistro[];
  prossimo: string | null;
}
interface Voci {
  azioni: { azione: string; quante: number }[];
  entita: { entita: string; quante: number }[];
  utenti: string[];
  prima: string | null;
  ultima: string | null;
  totale: number;
}

/** «UTENTE_MODIFICATO» si legge male; «Utente modificato» no. */
function leggibile(codice: string): string {
  const t = codice.replaceAll('_', ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/**
 * Il registro di sistema.
 *
 * Lo apre solo chi sta in `MASTRO_SUPERADMIN`: serve all'assistenza, non a chi
 * amministra l'azienda — un registro che il sorvegliato può leggere e a cui può
 * dare accesso non è un registro. Il controllo vero sta sulle rotte, che
 * rispondono 404 a chiunque altro; qui si evita solo di mostrare una voce di
 * menu che non porterebbe da nessuna parte.
 */
export function Registro() {
  const [entita, setEntita] = useState('');
  const [origine, setOrigine] = useState('');
  const [azione, setAzione] = useState('');
  const [utente, setUtente] = useState('');
  const [cerca, setCerca] = useState('');
  const [dal, setDal] = useState('');
  const [al, setAl] = useState('');
  // Digitando si filtra a ogni tasto: si aspetta che la persona abbia finito.
  const [cercaApplicato, setCercaApplicato] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setCercaApplicato(cerca.trim()), 350);
    return () => clearTimeout(t);
  }, [cerca]);

  const { organizzazione } = useSessione();
  const [altre, setAltre] = useState<VoceRegistro[]>([]);
  const [cursore, setCursore] = useState<string | null>(null);
  const [inCoda, setInCoda] = useState(false);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (entita) p.set('entita', entita);
    if (origine) p.set('origine', origine);
    if (azione) p.set('azione', azione);
    if (utente) p.set('utente', utente);
    if (cercaApplicato) p.set('cerca', cercaApplicato);
    if (dal) p.set('dal', dal);
    if (al) p.set('al', al);
    return p.toString();
  }, [entita, origine, azione, utente, cercaApplicato, dal, al]);

  const prima = useCarico(() => api.get<Pagina>(`/api/registro?${query}`), [query]);
  const voci = useCarico(() => api.get<Voci>('/api/registro/voci'), []);

  // Cambiando filtro si riparte da capo: le righe già caricate appartenevano a
  // un'altra domanda.
  useEffect(() => {
    setAltre([]);
    setCursore(null);
  }, [query]);

  const righe = [...(prima.dati?.righe ?? []), ...altre];
  const prossimo = cursore ?? prima.dati?.prossimo ?? null;

  const ancora = async () => {
    if (!prossimo) return;
    setInCoda(true);
    try {
      const p = await api.get<Pagina>(`/api/registro?${query}&prima=${encodeURIComponent(prossimo)}`);
      setAltre((a) => [...a, ...p.righe]);
      setCursore(p.prossimo);
    } finally {
      setInCoda(false);
    }
  };

  const filtroAttivo = Boolean(entita || origine || azione || utente || cercaApplicato || dal || al);

  return (
    <div className="pagina space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Registro di sistema</h1>
        <p className="mt-1 text-sm text-attenuato">
          Tutto ciò che è successo, in ordine: le azioni delle persone e i giri automatici insieme, perché è nella
          sequenza che si capisce cosa ha causato cosa.
          {voci.dati && voci.dati.totale > 0 && (
            <>
              {' '}
              <strong>{voci.dati.totale.toLocaleString('it-IT')}</strong> righe
              {voci.dati.prima && (
                <> dal {formattaData(voci.dati.prima.slice(0, 10), organizzazione.formatoData)}</>
              )}
              .
            </>
          )}
        </p>
      </div>

      <Scheda titolo="Filtri">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className="etichetta">Cerca</span>
            <input
              className="campo"
              placeholder="numero fattura, importo, parola della nota…"
              value={cerca}
              onChange={(e) => setCerca(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="etichetta">Cosa</span>
            <select className="campo" value={entita} onChange={(e) => setEntita(e.target.value)}>
              <option value="">tutto</option>
              {(voci.dati?.entita ?? []).map((e) => (
                <option key={e.entita} value={e.entita}>
                  {leggibile(e.entita)} ({e.quante})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="etichetta">Azione</span>
            <select className="campo" value={azione} onChange={(e) => setAzione(e.target.value)}>
              <option value="">tutte</option>
              {(voci.dati?.azioni ?? []).map((a) => (
                <option key={a.azione} value={a.azione}>
                  {leggibile(a.azione)} ({a.quante})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="etichetta">Chi</span>
            <select className="campo" value={utente} onChange={(e) => setUtente(e.target.value)}>
              <option value="">chiunque</option>
              {(voci.dati?.utenti ?? []).map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="etichetta">Origine</span>
            <select className="campo" value={origine} onChange={(e) => setOrigine(e.target.value)}>
              <option value="">persone e sistema</option>
              <option value="PERSONA">solo persone</option>
              <option value="SISTEMA">solo automatico</option>
            </select>
          </label>
          <label className="block">
            <span className="etichetta">Dal</span>
            <input type="date" className="campo" value={dal} onChange={(e) => setDal(e.target.value)} />
          </label>
          <label className="block">
            <span className="etichetta">Al</span>
            <input type="date" className="campo" value={al} onChange={(e) => setAl(e.target.value)} />
          </label>
          <div className="flex items-end">
            <button
              className="bottone-secondario"
              disabled={!filtroAttivo}
              onClick={() => {
                setEntita('');
                setOrigine('');
                setAzione('');
                setUtente('');
                setCerca('');
                setDal('');
                setAl('');
              }}
            >
              Togli i filtri
            </button>
          </div>
        </div>
      </Scheda>

      <Scheda titolo="Cosa è successo" senzaPadding>
        {prima.caricamento && righe.length === 0 ? (
          <Caricamento />
        ) : prima.errore ? (
          <Errore testo={prima.errore} riprova={prima.ricarica} />
        ) : righe.length === 0 ? (
          <Vuoto
            titolo="Nessuna riga"
            testo={filtroAttivo ? 'Nessun fatto corrisponde a questi filtri.' : 'Il registro è ancora vuoto.'}
          />
        ) : (
          <>
            <table className="dati">
              <thead>
                <tr>
                  <th className="w-40">Quando</th>
                  <th className="w-44">Chi</th>
                  <th className="w-56">Cosa</th>
                  <th>Dettaglio</th>
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <RigaRegistro key={r.id} r={r} formatoData={organizzazione.formatoData} />
                ))}
              </tbody>
            </table>
            {prossimo && (
              <div className="border-t border-inchiostro-200 p-3 text-center">
                <button className="bottone-secondario" disabled={inCoda} onClick={ancora}>
                  {inCoda ? 'Carico…' : 'Carica altre righe'}
                </button>
              </div>
            )}
          </>
        )}
      </Scheda>
    </div>
  );
}

function RigaRegistro({ r, formatoData }: { r: VoceRegistro; formatoData: Parameters<typeof formattaData>[1] }) {
  const [aperta, setAperta] = useState(false);
  const dettaglio = r.dettaglio ? JSON.stringify(r.dettaglio, null, 2) : null;
  // In riga il dettaglio sta su una sola riga e troncato: serve a riconoscere
  // la riga giusta, non a leggerla. Il testo intero si apre premendo.
  const sommario = r.dettaglio ? JSON.stringify(r.dettaglio) : '';

  return (
    <>
      <tr className={aperta ? 'bg-inchiostro-50' : undefined}>
        <td className="num whitespace-nowrap">
          {formattaData(r.creatoIl.slice(0, 10), formatoData)}{' '}
          <span className="text-inchiostro-500">{r.creatoIl.slice(11, 19)}</span>
        </td>
        <td>
          <span className="block truncate" title={r.utenteNome}>
            {r.utenteNome}
          </span>
          {r.origine === 'SISTEMA' ? (
            <Etichetta testo="automatico" />
          ) : (
            r.ip && <span className="text-[10px] text-inchiostro-500">{r.ip}</span>
          )}
        </td>
        <td>
          <span className="font-medium">{leggibile(r.azione)}</span>
          <span className="block text-xs text-inchiostro-500">{leggibile(r.entita)}</span>
        </td>
        <td>
          {r.nota && <span className="block text-sm">{r.nota}</span>}
          {dettaglio && (
            <button
              className="max-w-full truncate text-left font-mono text-xs text-inchiostro-500 underline decoration-dotted underline-offset-2"
              onClick={() => setAperta((a) => !a)}
              title="Apri il dettaglio"
            >
              {sommario}
            </button>
          )}
        </td>
      </tr>
      {aperta && dettaglio && (
        <tr className="bg-inchiostro-50">
          <td colSpan={4}>
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all text-xs">{dettaglio}</pre>
          </td>
        </tr>
      )}
    </>
  );
}
